/**
 * useVersionCheck
 *
 * Polls /version.json every 90 seconds (and on tab focus).
 * If the buildId changes since the page was first loaded, the page is
 * automatically reloaded so users always run the latest deployment.
 *
 * This acts as a safety net for the SW-based update flow:
 *   - If the SW detects a new deployment → controllerchange → reload (index.js)
 *   - If the SW misses it for any reason → version poll catches it within 90 s
 *
 * Only active in production (NODE_ENV === 'production').
 */
import { useEffect, useRef } from 'react';

const POLL_INTERVAL_MS = 90_000; // 90 seconds

export function useVersionCheck() {
  const knownBuildId = useRef(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    const check = async () => {
      try {
        const res = await fetch('/version.json?_=' + Date.now(), {
          cache: 'no-store',
        });
        if (!res.ok) return;

        const { buildId } = await res.json();
        if (!buildId || buildId === 'dev') return;

        if (knownBuildId.current === null) {
          // First successful poll — store the build ID of the current deployment.
          knownBuildId.current = buildId;
          return;
        }

        if (knownBuildId.current !== buildId) {
          // A new deployment is live — reload to get fresh assets.
          console.log('[VersionCheck] New deployment detected — reloading.', {
            was: knownBuildId.current,
            now: buildId,
          });
          window.location.reload();
        }
      } catch (_) {
        // Network error — silently ignore, retry on next interval.
      }
    };

    // Check immediately on mount.
    check();

    // Periodic check.
    const interval = setInterval(check, POLL_INTERVAL_MS);

    // Also check when the user returns to the tab.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
